import pin_g700 from '@/assets/icon/map-pin-filled-g700.svg';
import { body1, body2, body3, palette } from '@/modules/defines/styles';
import styled from 'styled-components';
import Input from '../Input';
import { NormalizedPin } from '@/modules/defines/interface';
import useFeedbackModal from '@/utils/hook/useProbabilityModal';
import { useRef, useEffect } from 'react';

interface SearchProps {
  isShowBubbleStartTooltip: boolean;
  isShowBubbleEndTooltip: boolean;
  handleOpenList: (type: 'start' | 'end') => void;
  setValue: React.Dispatch<
    React.SetStateAction<{ start: string; end: string }>
  >;
  value: { start: string; end: string };
  setStartPoint: React.Dispatch<React.SetStateAction<NormalizedPin | null>>;
  setEndPoint: React.Dispatch<React.SetStateAction<NormalizedPin | null>>;
  setCurrentPin: React.Dispatch<React.SetStateAction<NormalizedPin | null>>;
  handleFindPath: () => void;
  buttonValidation: () => boolean;
}
const Search = ({
  isShowBubbleStartTooltip,
  isShowBubbleEndTooltip,
  handleOpenList,
  setValue,
  value,
  setStartPoint,
  setEndPoint,
  setCurrentPin,
  handleFindPath,
  buttonValidation,
}: SearchProps) => {
  const { showModal } = useFeedbackModal();
  const hasShownModalRef = useRef(false);

  useEffect(() => {
    if (buttonValidation() && !hasShownModalRef.current) {
      showModal(40);
      hasShownModalRef.current = true;
    }
  }, [buttonValidation, showModal]);

  return (
    <FlexCol>
      <InputWrapper>
        <Label htmlFor="start">
          <img width={20} height={20} src={pin_g700} alt="pin" />
          출발
        </Label>
        <Input
          onFocus={(e) => {
            e.stopPropagation();
            handleOpenList('start');
          }}
          value={value.start}
          onChange={(e) => {
            setValue({ ...value, start: e.target.value });
            if (e.target.value === '') {
              setStartPoint(null);
              setCurrentPin(null);
            }
          }}
          $extraPadding="0.625rem 0.75rem 0.625rem 5rem"
          id="start"
          $fontWeight={400}
          type="search"
          placeholder="출발지 입력"
        />
      </InputWrapper>
      <BubbleWrapper
        $isShow={isShowBubbleStartTooltip && value.start === ''}
        $top="-16px"
        $left="3.75rem"
      >
        <Bubble $type="start">
          출발지를 입력해주세요
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="7"
            viewBox="0 0 12 7"
            fill="none"
          >
            <path
              d="M7.6 5.86667C6.8 6.93333 5.2 6.93333 4.4 5.86667L-6.99382e-07 1.04907e-06L12 0L7.6 5.86667Z"
              fill="#85121E"
            />
          </svg>
        </Bubble>
      </BubbleWrapper>
      <InputWrapper>
        <BubbleWrapper
          $isShow={isShowBubbleEndTooltip && value.end === ''}
          $top="auto"
          $bottom="-30px"
          $left="3.75rem"
        >
          <Bubble $type="end">
            도착지를 입력해주세요
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="7"
              viewBox="0 0 12 7"
              fill="none"
            >
              <path
                d="M4.4 1.13333C5.2 0.0666671 6.8 0.0666668 7.6 1.13333L12 7H0L4.4 1.13333Z"
                fill="#85121E"
              />
            </svg>
          </Bubble>
        </BubbleWrapper>
        <Label htmlFor="destination">
          <img width={20} height={20} src={pin_g700} alt="pin" />
          도착
        </Label>
        <Input
          onFocus={(e) => {
            e.stopPropagation();
            handleOpenList('end');
          }}
          value={value.end}
          onChange={(e) => {
            setValue({ ...value, end: e.target.value });
            if (e.target.value === '') {
              setEndPoint(null);
              setCurrentPin(null);
            }
          }}
          $extraPadding="0.625rem 0.75rem 0.625rem 5rem"
          $fontWeight={400}
          type="search"
          placeholder="도착지 입력"
        />
      </InputWrapper>

      <XRButton
        onClick={handleFindPath}
        $isComplete={buttonValidation()}
        type="button"
      >
        XR 길찾기
      </XRButton>
    </FlexCol>
  );
};

const InputWrapper = styled.div`
  position: relative;
`;

const BubbleWrapper = styled.div<{
  $isShow: boolean;
  $top: string;
  $left: string;
  $bottom?: string;
}>`
  display: ${(props) => (props.$isShow ? 'block' : 'none')};
  position: absolute;
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
  bottom: ${(props) => props.$bottom};
  width: 9.125rem;
  z-index: 22;
`;
const Bubble = styled.div<{
  $type: 'start' | 'end';
}>`
  position: relative;
  height: 1.875rem;
  background: ${palette.secondaryRed700};

  color: white;
  border-radius: 1000px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  ${body3};

  svg {
    position: absolute;
    bottom: ${(props) => (props.$type === 'start' ? ' -6px' : 'auto')};
    top: ${(props) => (props.$type === 'end' ? ' -6px' : 'auto')};
    left: 50%;
  }
`;
const Label = styled.label`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 0.12rem;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  ${body2};
  font-weight: 700;
  color: ${palette.gray700};
`;

const XRButton = styled.button<{
  $isComplete: boolean;
}>`
  display: ${(props) => (props.$isComplete ? 'block' : 'none')};
  ${body1};
  width: 100%;
  color: ${palette.white};
  font-weight: 700;
  height: 3.125rem;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${palette.secondaryRed700};
`;

const FlexCol = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.625rem 1.5625rem;
`;

export default Search;
